// supportChatTime — helpers de fecha/hora para la burbuja de chat de soporte.
// Todo client-side (Intl/toLocaleString). El horario hábil se evalúa en la zona de Argentina,
// independiente de la zona del navegador del usuario.

const AR_TZ = 'America/Argentina/Buenos_Aires';

// ── Horario hábil: L-V 9:00-19:00 hora de Argentina ─────────────────────────────
// Derivamos día de semana y hora en AR_TZ a partir de la hora del navegador. Usamos
// Intl.DateTimeFormat con timeZone para no depender de la zona local del usuario.
export function isBusinessHours(now = new Date()) {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: AR_TZ,
            weekday: 'short',
            hour: 'numeric',
            hour12: false,
        }).formatToParts(now);
        const weekday = parts.find((p) => p.type === 'weekday')?.value;
        const hourStr = parts.find((p) => p.type === 'hour')?.value;
        const hour = parseInt(hourStr, 10);
        // 'Sun' 'Mon' ... 'Sat'. Hábil = Mon..Fri.
        const isWeekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].includes(weekday);
        // Intl con hour12:false puede devolver "24" a la medianoche; normalizamos a 0.
        const normalizedHour = hour === 24 ? 0 : hour;
        return isWeekday && normalizedHour >= 9 && normalizedHour < 19;
    } catch (_) {
        // Ante cualquier problema con Intl asumimos horario hábil (mensaje optimista por defecto).
        return true;
    }
}

// ── Formatos de hora / fecha (zona local del navegador) ─────────────────────────

// Hora corta "HH:mm" (locale del navegador, 24h estable para no depender del AM/PM).
export function formatTimeShort(isoString) {
    const date = parseDate(isoString);
    if (!date) return '';
    try {
        return new Intl.DateTimeFormat(undefined, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).format(date);
    } catch (_) {
        return '';
    }
}

// Etiqueta de fecha para el separador entre días: "Hoy", "Ayer" o fecha corta (i18n para
// hoy/ayer via el caller — acá devolvemos {kind:'today'|'yesterday'|'date', label}).
export function dayLabelParts(isoString) {
    const date = parseDate(isoString);
    if (!date) return {kind: 'date', label: ''};
    const today = startOfDay(new Date());
    const target = startOfDay(date);
    const diffDays = Math.round((today - target) / (24 * 60 * 60 * 1000));
    if (diffDays === 0) return {kind: 'today', label: ''};
    if (diffDays === 1) return {kind: 'yesterday', label: ''};
    let label;
    try {
        label = new Intl.DateTimeFormat(undefined, {
            day: 'numeric',
            month: 'short',
            year: target.getFullYear() === today.getFullYear() ? undefined : 'numeric',
        }).format(date);
    } catch (_) {
        label = '';
    }
    return {kind: 'date', label};
}

// Clave de día (YYYY-MM-DD en local) para agrupar mensajes y decidir separadores.
export function dayKey(isoString) {
    const date = parseDate(isoString);
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

// Fecha relativa corta para la lista de conversaciones: "ahora", "hace 5 min", "hace 2 h",
// "ayer", o fecha corta. Devuelve {kind, ...} para que el caller ponga la i18n.
export function relativeTimeParts(isoString) {
    const date = parseDate(isoString);
    if (!date) return {kind: 'date', label: '', value: 0};
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return {kind: 'now'};
    if (diffMin < 60) return {kind: 'minutes', value: diffMin};
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24 && startOfDay(now).getTime() === startOfDay(date).getTime()) {
        return {kind: 'hours', value: diffHours};
    }
    const startToday = startOfDay(now);
    const startTarget = startOfDay(date);
    const diffDays = Math.round((startToday - startTarget) / (24 * 60 * 60 * 1000));
    if (diffDays === 1) return {kind: 'yesterday'};
    let label;
    try {
        label = new Intl.DateTimeFormat(undefined, {
            day: 'numeric',
            month: 'short',
            year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
        }).format(date);
    } catch (_) {
        label = '';
    }
    return {kind: 'date', label};
}

function parseDate(isoString) {
    if (!isoString) return null;
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return null;
    return date;
}

function startOfDay(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}
